import { type ReactNode, createContext, use, useLayoutEffect, useMemo, useRef } from "react";
import axios, { type AxiosError, type AxiosInstance } from "axios";
import { useOktaAuth } from "@okta/okta-react";

const AxiosContext = createContext<AxiosInstance | null>(null);

// Optional chaining on `.env` itself: Vite always defines it at build time, but non-Vite
// module loaders (e.g. Playwright's `@unit` test runner) leave `import.meta.env` undefined.
const baseURL = import.meta.env?.VITE_PUBLIC_API_GATEWAY_BASE_URL ?? "";

const setAuthHeaders = (config: { headers?: unknown }, token: string) => {
  const headers = (config.headers ?? {}) as Record<string, unknown>;
  config.headers = headers;
  headers["Authorization"] = `Bearer ${token}`;
};

const AxiosProvider = ({ children }: { readonly children: ReactNode }) => {
  const { oktaAuth } = useOktaAuth();
  const interceptorId = useRef<number | null>(null);
  const responseInterceptorId = useRef<number | null>(null);
  const inFlightToken = useRef<Promise<string | null> | null>(null);
  /** Whether `inFlightToken` was started as a forced refresh (see `getToken` below). */
  const inFlightTokenIsForced = useRef(false);

  const axiosClient = useMemo(() => axios.create({ baseURL }), []);

  useLayoutEffect(() => {
    if (interceptorId.current !== null) {
      axiosClient.interceptors.request.eject(interceptorId.current);
      interceptorId.current = null;
    }

    if (responseInterceptorId.current !== null) {
      axiosClient.interceptors.response.eject(responseInterceptorId.current);
      responseInterceptorId.current = null;
    }

    const fetchToken = async (forceRefresh: boolean): Promise<string | null> => {
      try {
        if (forceRefresh) {
          await oktaAuth.tokenManager.renew("accessToken");
        }

        const token = await oktaAuth.tokenManager.get("accessToken");
        return token && "accessToken" in token ? token.accessToken : null;
      } catch {
        return null;
      }
    };

    const getToken = (forceRefresh: boolean) => {
      // A forced refresh already in flight satisfies both forced and non-forced callers; a
      // non-forced fetch in flight only satisfies non-forced callers (a 401 needs an actually
      // renewed token, not whatever the in-progress plain `.get()` returns).
      if (inFlightToken.current && (inFlightTokenIsForced.current || !forceRefresh)) {
        return inFlightToken.current;
      }

      inFlightTokenIsForced.current = forceRefresh;
      inFlightToken.current = fetchToken(forceRefresh).finally(() => {
        inFlightToken.current = null;
        inFlightTokenIsForced.current = false;
      });

      return inFlightToken.current;
    };

    interceptorId.current = axiosClient.interceptors.request.use(
      async (config) => {
        const token = await getToken(false);
        if (token) {
          setAuthHeaders(config, token);
        }

        return config;
      },
      (error) => {
        throw error;
      }
    );

    responseInterceptorId.current = axiosClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const originalRequest = error.config;
          if (originalRequest && !(originalRequest as unknown as { retried?: boolean }).retried) {
            (originalRequest as unknown as { retried?: boolean }).retried = true;
            const fresh = await getToken(true);
            if (fresh) {
              const retryConfig = originalRequest as Parameters<AxiosInstance["request"]>[0];
              setAuthHeaders(retryConfig, fresh);
              return axiosClient.request(retryConfig);
            }
          }
        }

        throw error;
      }
    );

    return () => {
      if (interceptorId.current !== null) {
        axiosClient.interceptors.request.eject(interceptorId.current);
      }

      if (responseInterceptorId.current !== null) {
        axiosClient.interceptors.response.eject(responseInterceptorId.current);
      }
    };
  }, [axiosClient, oktaAuth]);

  return <AxiosContext value={axiosClient}>{children}</AxiosContext>;
};

const useAxios = (): AxiosInstance => {
  const context = use(AxiosContext);
  if (!context) {
    throw new Error("useAxios must be used within an AxiosProvider");
  }

  return context;
};

export { AxiosProvider, useAxios, baseURL };
